import { useState } from 'react'
import { Panel, SectionHeader } from '@/components/erp/primitives'
import { ErrorStrip, Forbidden, LoadingBlock } from '@/shared/ui/states'
import { useCan } from '@/shared/auth/session'
import { useLoad } from '@/features/administration/hooks'
import { explain } from '@/shared/api/problem'
import { fmtNumber } from '@/shared/lib/format'
import {
  expenditureStatement, exportStatement, type ExpenditureStatement, type StatementHeading,
} from './api'

/**
 * The expenditure statement — what a candidate puts in front of them to file.
 *
 * ## The caveat is on the page, not under it
 *
 * The Election Campaign Financing Act asks for contributions received AND expenditure
 * incurred. Axis holds the second. That is said at the top, in a bordered block nobody can
 * scroll past, and it is repeated as the first line of the exported CSV — because a CSV gets
 * opened in a spreadsheet and forwarded without whatever page it came from.
 *
 * A document that looks complete and is not is worse than no document, because it is the one
 * that gets filed.
 *
 * ## Every heading carries its lines
 *
 * A total with nothing behind it cannot be defended. "What is this 1.4 million" has to have
 * an answer in the same document, so each heading expands to its entries — reference, date,
 * payee, how it was paid and the payment reference.
 */
export function StatementPage() {
  const canAnalyse = useCan('finance:analyse')
  const [months, setMonths] = useState(12)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [exported, setExported] = useState<string | null>(null)

  const to = new Date()
  const from = new Date(to)
  from.setMonth(from.getMonth() - months)
  const iso = (d: Date) => d.toISOString().slice(0, 10)
  const range = { from: iso(from), to: iso(to) }

  const statement = useLoad(() => expenditureStatement(range), [months])

  async function download() {
    setBusy(true)
    setError(null)
    try {
      const file = await exportStatement(range)
      setExported(file.filename)
    } catch (e) {
      setError(explain(e))
    } finally {
      setBusy(false)
    }
  }

  if (!canAnalyse) return <Forbidden what="The expenditure statement" />

  const d = statement.data

  return (
    <div className="space-y-4">
      <SectionHeader
        title="Expenditure statement"
        description="Every shilling the campaign spent, by statutory heading, with the entries
                     behind each total. Produced for the period you choose."
        right={(
          <div className="flex items-center gap-2">
            <select value={months} onChange={(e) => setMonths(Number(e.target.value))}
                    className="h-9 rounded border border-border bg-background px-2 text-erp-sm">
              <option value={3}>Last 3 months</option>
              <option value={6}>Last 6 months</option>
              <option value={12}>Last 12 months</option>
              <option value={24}>Last 2 years</option>
            </select>
            <button type="button" className="erp-btn-secondary h-9 text-erp-sm"
                    disabled={busy || !d} onClick={download}>
              {busy ? 'Preparing…' : 'Download CSV'}
            </button>
          </div>
        )}
      />

      <ErrorStrip message={error ?? statement.error} />

      {exported ? (
        <div className="rounded border border-border bg-muted/40 px-3 py-2 text-erp-sm">
          Prepared <b>{exported}</b> — find it under Files. Taking a copy is recorded in the
          audit trail.
        </div>
      ) : null}

      {statement.loading && !d ? <LoadingBlock label="Preparing the statement…" />
        : !d ? null : <Statement d={d} />}
    </div>
  )
}

function Statement({ d }: { d: ExpenditureStatement }) {
  const money = (n: number | null | undefined) =>
    n == null ? '—' : `${d.currency ?? ''} ${fmtNumber(Math.round(n))}`.trim()

  return (
    <div className="space-y-4">
      {/* Not a footnote. The one thing somebody must read before filing anything. */}
      <div className="rounded border-2 border-amber-500/70 bg-amber-50 px-4 py-3
                      dark:bg-amber-950/30">
        <p className="text-erp-sm font-medium">Before you file this</p>
        <p className="mt-1 text-erp-sm">{d.contributionsNote}</p>
      </div>

      <Panel>
        <div className="p-4">
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <h3 className="text-erp-lg font-semibold">Statement of campaign expenditure</h3>
            <span className="text-erp-xs text-muted-foreground tabular-nums">
              Prepared {new Date(d.preparedAt).toLocaleString()}
            </span>
          </div>
          <p className="mt-1 text-erp-sm text-muted-foreground">
            {d.electionName ? <>{d.electionName} · </> : null}
            {d.from} to {d.to}
          </p>

          {d.mixedCurrencyReason ? (
            <p className="mt-3 text-erp-sm text-destructive">{d.mixedCurrencyReason}</p>
          ) : (
            <dl className="mt-4 grid gap-x-6 gap-y-2 sm:grid-cols-2 lg:grid-cols-4">
              <Figure label="Total recorded" value={money(d.gross)}
                sub={`${fmtNumber(d.entries)} entries`} />
              <Figure label="Corrected" value={money(d.reversed)}
                sub={`${d.reversals} reversing ${d.reversals === 1 ? 'entry' : 'entries'}`} />
              <Figure label="Total expenditure" value={money(d.net)} strong
                sub="net of corrections" />
              <Figure label="Without a receipt" value={money(d.withoutReceiptAmount)}
                sub={`${d.withoutReceipt} of ${fmtNumber(d.entries)} entries`}
                warn={d.withoutReceipt > 0} />
            </dl>
          )}
        </div>
      </Panel>

      {d.headings.length === 0 ? (
        <Panel title="Nothing recorded in this period">
          <p className="p-3 text-erp-sm text-muted-foreground">
            No campaign spending was recorded between {d.from} and {d.to}.
          </p>
        </Panel>
      ) : d.headings.map((heading) => (
        <Heading key={heading.categoryCode} heading={heading} currency={d.currency} />
      ))}
    </div>
  )
}

function Heading({ heading, currency }: { heading: StatementHeading, currency: string | null }) {
  const [open, setOpen] = useState(false)
  const money = (n: number | null) =>
    n == null ? '—' : `${currency ?? ''} ${fmtNumber(Math.round(n))}`.trim()

  return (
    <Panel>
      <button type="button" onClick={() => setOpen((v) => !v)}
              className="flex w-full items-baseline justify-between gap-3 px-4 py-3 text-left
                         hover:bg-muted/40">
        <span>
          <span className="text-erp-md font-medium">{heading.label}</span>
          <span className="ml-2 text-erp-xs text-muted-foreground">
            {heading.entries} {heading.entries === 1 ? 'entry' : 'entries'}
            {heading.share != null ? ` · ${Math.round(heading.share * 100)}% of total` : ''}
          </span>
        </span>
        <span className="shrink-0 text-erp-md font-medium tabular-nums">
          {money(heading.amount)}
          <span className="ml-2 text-erp-xs text-muted-foreground">
            {open ? '▾' : '▸'}
          </span>
        </span>
      </button>

      {open && (
        <div className="axis-table-scroll border-t border-border">
          <table className="w-full text-erp-sm">
            <thead className="bg-muted/40 text-erp-xs uppercase text-muted-foreground">
              <tr>
                <th className="px-3 py-2 text-left">Reference</th>
                <th className="px-3 py-2 text-left">Date</th>
                <th className="px-3 py-2 text-left">Paid to</th>
                <th className="px-3 py-2 text-left">For</th>
                <th className="px-3 py-2 text-right">Amount</th>
                <th className="px-3 py-2 text-left">Paid by</th>
                <th className="px-3 py-2 text-left">Receipt</th>
              </tr>
            </thead>
            <tbody>
              {heading.lines.map((line) => (
                <tr key={line.reference}
                    className={'border-t border-border '
                      + (line.reversal ? 'bg-muted/40 text-muted-foreground' : '')}>
                  <td className="px-3 py-2">
                    {line.reference}
                    {line.reversal && line.reversesReference ? (
                      <div className="text-erp-xs">reverses {line.reversesReference}</div>
                    ) : null}
                  </td>
                  <td className="px-3 py-2 tabular-nums">{line.incurredOn}</td>
                  <td className="px-3 py-2">{line.payee ?? '—'}</td>
                  <td className="px-3 py-2">
                    {line.purpose ?? '—'}
                    {line.unitName ? (
                      <div className="text-erp-xs text-muted-foreground">{line.unitName}</div>
                    ) : null}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums">
                    {fmtNumber(Math.round(line.amount))}
                  </td>
                  <td className="px-3 py-2">
                    {line.paymentMethodCode.toLowerCase().replace(/_/g, ' ')}
                    {line.paymentReference ? (
                      <div className="text-erp-xs text-muted-foreground tabular-nums">
                        {line.paymentReference}
                      </div>
                    ) : null}
                  </td>
                  <td className="px-3 py-2">
                    {line.reversal ? '—'
                      : line.receipts === 0
                        ? <span className="text-destructive">none</span>
                        : line.receipts}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Panel>
  )
}

function Figure({ label, value, sub, strong, warn }: {
  label: string
  value: string
  sub?: string
  strong?: boolean
  warn?: boolean
}) {
  return (
    <div>
      <dt className="text-erp-xs uppercase tracking-wide text-muted-foreground">{label}</dt>
      <dd className={'tabular-nums ' + (strong ? 'text-erp-lg font-semibold' : 'text-erp-md')
        + (warn ? ' text-destructive' : '')}>
        {value}
      </dd>
      {sub ? <dd className="text-erp-xs text-muted-foreground">{sub}</dd> : null}
    </div>
  )
}
